import * as React from 'react';
import {Route} from 'react-router-dom';
// @ts-expect-error
import type {RouterHistory, Location} from 'react-router-dom';

import type {User} from 'nutshell-core/types';

import {ActivityModalRoot} from '../ui/activity-modal';
import {EntityModal} from '../ui/creator/entity-modal';
import {ScheduledEmailEditModalRoot} from '../ui/email/mailbox/scheduled-mail/scheduled-email-edit-modal-root';
import {EmailTemplateDesignerModal} from '../ui/email/templates/email-template-designer-modal';
import {NewFormModal} from '../ui/forms/form-create/new-form-modal';
import {InvoiceModal} from '../ui/lead/sales-documents/invoices/components';
import {QuoteModal} from '../ui/lead/sales-documents/quotes';
import {LeaveAReviewModal} from '../ui/leave-a-review/leave-a-review-modal';
import {ReferAFriendModal} from '../ui/refer-a-friend/refer-a-friend-modal';
import {CalendarConnectModalRoot} from '../ui/setup/calendar/calendar-connect-modal/calendar-connect-modal-root';
import {ConnectEmailModalRoot} from '../ui/setup/email/connect-email-modal/connect-email-modal-root';
import {ConnectMfaModalRoot} from '../ui/setup/mfa/connect-mfa-modal-root';
import {ConnectPhoneIntegrationModalRoot} from '../ui/setup/phone/connect-phone-integration-modal-root';
import {TimelineModalRoot} from '../ui/timeline/modals/timeline-modal-root';

const MODAL_HASH_PATTERNS = [
    /#\/connect-email/,
    /#\/connect-calendar/,
    /#\/connect-mfa/,
    /#\/connect-phone-integration/,
    /#\/create\//,
    /#\/log\/activities/,
    /#\/[0-9]+-activities/,
    /#\/quotes\//,
    /#\/invoices\//,
    /#\/(note|email|ticketmessage|chat|mcfxSession)\/[0-9]+/,
    /#\/templates\/(new|edit)/,
    /#\/edit-scheduled-email\/\d+-emailsQueued/,
    /#leave-a-review/,
    /#refer-a-friend/,
];

type Props = {
    history: RouterHistory;
    location: Location;
    user: User | null | undefined;
};

export function AppRoutesModals(props: Props) {
    const {history: appHistory, location: appLocation} = props;

    const previousLocationRef = React.useRef(appLocation);

    React.useEffect(() => {
        if (appHistory.action !== 'POP' && (!appLocation.state || !appLocation.state.modal)) {
            previousLocationRef.current = appLocation;
        }
    }, [appHistory.action, appLocation]);

    const handleModalClose = (history: RouterHistory, hashRegex: RegExp) => {
        const currentHash = window.location.hash;
        const isRoutingToDifferentModal = MODAL_HASH_PATTERNS.some((pattern) => {
            return currentHash.match(pattern) && !currentHash.match(hashRegex);
        });

        if (isRoutingToDifferentModal) {
            return;
        }

        // If the page was loaded with a creator hash, clear out the hash,
        // Otherwise, go back to the location before the modal was opened.
        history.replace(
            appLocation.hash.match(hashRegex)
                ? {
                      hash: '',
                      search: appLocation.search,
                  }
                : previousLocationRef.current
        );
    };

    const canUseSalesDocuments = props.user ? props.user.permissions.canUseSalesDocuments : false;

    return (
        <>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    // create/activities
                    // create/tasks
                    // create/leads
                    // create/contacts
                    // create/accounts
                    // create/quotes
                    // create/invoices
                    const hashRegex =
                        /#\/create\/(activities|tasks|leads|contacts|accounts|quotes|invoices)(?:\/([0-9]+-(?:contacts|accounts|leads)))?/;
                    const found = location.hash.match(hashRegex);
                    const entityType = found && found[1];

                    if (
                        found &&
                        entityType === 'activities' &&
                        NutClientConfig.hasNewActivityModalEverywhereFF
                    ) {
                        return <></>;
                    }

                    if (
                        found &&
                        (entityType === 'quotes' || entityType === 'invoices') &&
                        !canUseSalesDocuments
                    ) {
                        return <></>;
                    }

                    if (found) {
                        return (
                            // @ts-expect-error
                            <EntityModal
                                isOpen={Boolean(found)}
                                entityType={entityType}
                                onClose={() => {
                                    handleModalClose(history, hashRegex);
                                }}
                                entityId={null}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    // #/quotes/{id}
                    const hashRegex = /#\/quotes\/[0-9]*/;
                    const found = location.hash.match(hashRegex);
                    const digits = location.hash.split('/')[2];

                    if (found && canUseSalesDocuments) {
                        return (
                            <QuoteModal
                                isOpen={Boolean(found)}
                                quoteId={`${digits}-quotes`}
                                onClose={() => {
                                    handleModalClose(history, hashRegex);
                                }}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    // #/invoices/{id}
                    const hashRegex = /#\/invoices\/[0-9]*/;
                    const found = location.hash.match(hashRegex);
                    const digits = location.hash.split('/')[2];

                    if (found && canUseSalesDocuments) {
                        return (
                            <InvoiceModal
                                isOpen={Boolean(found)}
                                // The API id is the same format as quotes
                                // because we are using the same table for
                                // quotes and invoices
                                invoiceId={`${digits}-quotes`}
                                onClose={() => {
                                    handleModalClose(history, hashRegex);
                                }}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    const hashRegex = /#\/connect-email/;
                    const found = location.hash.match(hashRegex);

                    return (
                        <ConnectEmailModalRoot
                            isOpen={Boolean(found)}
                            onClose={() => {
                                handleModalClose(history, hashRegex);
                            }}
                        />
                    );
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    const hashRegex = /#\/connect-calendar/;
                    const found = location.hash.match(hashRegex);

                    return (
                        <CalendarConnectModalRoot
                            isOpen={Boolean(found)}
                            onClose={() => {
                                handleModalClose(history, hashRegex);
                            }}
                        />
                    );
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    // create/forms
                    const hashRegex = /#\/create\/forms/;
                    const found = location.hash.match(hashRegex);

                    if (found) {
                        return (
                            <NewFormModal
                                isOpen={true}
                                onClose={() => {
                                    handleModalClose(history, hashRegex);
                                }}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    const hashRegex = /#\/connect-mfa/;
                    const found = location.hash.match(hashRegex);

                    if (found) {
                        return (
                            <ConnectMfaModalRoot
                                isOpen={Boolean(found)}
                                onClose={() => {
                                    handleModalClose(history, hashRegex);
                                }}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location}) => {
                    // GREP TEST
                    // #/email/{id}/ai
                    const hashRegex = /#\/(email)\/[0-9]*\/(ai)/;
                    const digits = location.hash.split('/')[2];

                    const id = `${digits}-emails`;
                    const found = location.hash.match(hashRegex);

                    if (found) {
                        window.location.href = `/ai/email-replies?id=${id}`;
                    }

                    return <></>;
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    // #/note/{id}
                    // #/email/{id}
                    // #/chat/{id}
                    // #/ticketmessage/{id}
                    // #/mcfxSession/{id}
                    const hashRegex = /#\/(note|email|ticketmessage|chat|mcfxSession)\/[0-9]*/;
                    const digits = location.hash.split('/')[2];
                    const hashedEntity = location.hash.split('/')[1];
                    const entity =
                        hashedEntity === 'ticketmessage' ? 'ticketMessages' : `${hashedEntity}s`;
                    const id = `${digits}-${entity}`;
                    const found = location.hash.match(hashRegex);

                    if (found) {
                        return (
                            <TimelineModalRoot
                                id={id}
                                isOpen={Boolean(found)}
                                onClose={() => {
                                    handleModalClose(history, hashRegex);
                                }}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    // #/templates/new
                    // #/templates/edit
                    const hashRegex = /#\/templates\/(new|edit)*/;
                    const found = location.hash.match(hashRegex);
                    if (found) {
                        const params = location.hash.split('?')[1];
                        let sentEmailId;
                        let sequenceId;
                        if (params && params.includes('sentEmailId')) {
                            sentEmailId = params.split('=')[1];
                        } else if (params && params.includes('sequenceId')) {
                            sequenceId = params.split('=')[1];
                        }

                        return (
                            <EmailTemplateDesignerModal
                                isOpen={Boolean(found)}
                                onClose={() => handleModalClose(history, hashRegex)}
                                onChildTemplateSaved={() => handleModalClose(history, hashRegex)}
                                sequenceTemplateId={sequenceId}
                                sentEmailId={sentEmailId}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route path='/tasks/:taskId/:action?'>
                {({match, history, location}) => {
                    // @ts-expect-error
                    if (match && location.state && location.state.modal) {
                        // This should only be shown when we're in existing task modal mode.
                        return (
                            <EntityModal
                                isOpen={true}
                                entityType='tasks'
                                onClose={() => {
                                    history.replace(previousLocationRef.current);
                                }}
                                entityId={match && match.params.taskId}
                                disabled={match && match.params.action !== 'edit'}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    const hashRegex = /#\/edit-scheduled-email\/\d+-emailsQueued/;
                    const found = location.hash.match(hashRegex);

                    return (
                        <ScheduledEmailEditModalRoot
                            isOpen={Boolean(found)}
                            onClose={() => {
                                handleModalClose(history, hashRegex);
                            }}
                        />
                    );
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // These are handled by the same Route so that we can switch
                    // the hash on activity creation without unmounting and
                    // remounting the root, allowing us to have a smooth transition

                    // GREP TEST
                    // #/create/activities
                    // #/log/activities
                    // #/{id}-activities
                    const createActivityRegex =
                        /#\/create\/activities(?:\/([0-9]+-(?:contacts|accounts|leads|activities)))?/;
                    const logActivityRegex =
                        /#\/log\/activities(?:\/([0-9]+-(?:contacts|accounts|leads|activities)))?/;
                    const existingActivityRegex = /#\/([0-9]*-activities)/;

                    const createMatch = location.hash.match(createActivityRegex);
                    const logMatch = location.hash.match(logActivityRegex);
                    const existingMatch = location.hash.match(existingActivityRegex);

                    const shouldHandleCreate =
                        createMatch && NutClientConfig.hasNewActivityModalEverywhereFF;
                    const shouldHandleLog =
                        logMatch && NutClientConfig.hasNewActivityModalEverywhereFF;
                    const shouldHandleExisting =
                        existingMatch && NutClientConfig.hasNewActivityModalFF;

                    const found = shouldHandleCreate || shouldHandleLog || shouldHandleExisting;

                    if (found) {
                        let activityId = '';
                        let relatedEntityId = '';
                        let variant = 'create';
                        // @ts-expect-error
                        let hashRegex;

                        if (shouldHandleCreate && createMatch) {
                            // @ts-expect-error
                            relatedEntityId = createMatch[1];
                            hashRegex = createActivityRegex;
                            variant = 'create';
                        } else if (shouldHandleLog && logMatch) {
                            // @ts-expect-error
                            relatedEntityId = logMatch[1];
                            hashRegex = logActivityRegex;
                            variant = 'log';
                        } else if (shouldHandleExisting && existingMatch) {
                            // @ts-expect-error
                            activityId = existingMatch[1];
                            hashRegex = existingActivityRegex;
                            variant = 'update';
                        }

                        return (
                            <ActivityModalRoot
                                history={history}
                                isOpen={Boolean(found)}
                                onClose={() => {
                                    // @ts-expect-error
                                    handleModalClose(history, hashRegex);
                                }}
                                activityId={activityId}
                                relatedEntityId={relatedEntityId}
                                // @ts-expect-error
                                variant={variant}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    const hashRegex = /#\/connect-phone-integration/;
                    const found = location.hash.match(hashRegex);
                    if (found) {
                        return (
                            <ConnectPhoneIntegrationModalRoot
                                isOpen={Boolean(found)}
                                onClose={() => {
                                    handleModalClose(history, hashRegex);
                                }}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    // #leave-a-review
                    const hashRegex = /#leave-a-review/;
                    const found = location.hash.match(hashRegex);
                    if (found) {
                        return (
                            <LeaveAReviewModal
                                onClose={() => {
                                    handleModalClose(history, hashRegex);
                                }}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
            <Route>
                {({location, history}) => {
                    // GREP TEST
                    // #refer-a-friend
                    const hashRegex = /#refer-a-friend/;
                    const found = location.hash.match(hashRegex);
                    if (found) {
                        return (
                            <ReferAFriendModal
                                isOpen={true}
                                onClose={() => {
                                    handleModalClose(history, hashRegex);
                                }}
                            />
                        );
                    } else {
                        return <></>;
                    }
                }}
            </Route>
        </>
    );
}
